/**
 * Custom hook for document section navigation
 * Story 280: More reliably view stories in the workspace
 *
 * Encapsulates keyboard navigation logic between sections:
 * - Navigate up/down between sections
 * - Navigate between title and body within a section
 * - Create new sections on Shift+Enter at the end
 */

import { useCallback } from 'react';
import type { DocumentSection } from '../types/document';

type ActiveField = { sectionKey: string; field: 'title' | 'body' };

interface UseDocumentNavigationProps {
  sections: DocumentSection[];
  setActiveField: (field: ActiveField | null) => void;
  onInsert: (afterKey: string | null) => string;
}

interface DocumentNavigationHandlers {
  /** Navigate from body to next section's title (down arrow) */
  handleNavigateNextFromBody: (currentKey: string) => void;
  /** Navigate from body, or create new section at end (Shift+Enter) */
  handleShiftReturnFromBody: (currentKey: string) => void;
  /** Navigate from body to current section's title (up arrow) */
  handleNavigateToTitle: (currentKey: string) => void;
  /** Navigate from title to current section's body (down arrow) */
  handleNavigateToBody: (currentKey: string) => void;
  /** Navigate from title to previous section's body (up arrow) */
  handleNavigatePrevFromTitle: (currentKey: string) => void;
  /** Insert new section and activate it for editing */
  handleInsert: (afterKey: string | null) => void;
}

/**
 * Hook for document section navigation
 *
 * @param props.sections - Current document sections
 * @param props.setActiveField - Callback to set the active field
 * @param props.onInsert - Callback to insert a new section, returns new section key
 * @returns Navigation handler functions
 */
export function useDocumentNavigation({
  sections,
  setActiveField,
  onInsert,
}: UseDocumentNavigationProps): DocumentNavigationHandlers {
  // Insert a new section and activate it for editing
  // For the first section, activate body; otherwise activate title
  const handleInsert = useCallback((afterKey: string | null) => {
    const isFirstSection = sections.length === 0;
    const newKey = onInsert(afterKey);
    setActiveField({ sectionKey: newKey, field: isFirstSection ? 'body' : 'title' });
  }, [onInsert, sections.length, setActiveField]);

  // Navigate from body to next section's title (down arrow - no creation at end)
  const handleNavigateNextFromBody = useCallback((currentKey: string) => {
    const currentIndex = sections.findIndex(s => s.section_key === currentKey);
    if (currentIndex < sections.length - 1) {
      setActiveField({ sectionKey: sections[currentIndex + 1].section_key, field: 'title' });
    }
    // At last section, do nothing - don't create new section on arrow key
  }, [sections, setActiveField]);

  // Shift+Enter: navigate to next section, or create new if at last one
  const handleShiftReturnFromBody = useCallback((currentKey: string) => {
    const currentIndex = sections.findIndex(s => s.section_key === currentKey);
    if (currentIndex < sections.length - 1) {
      setActiveField({ sectionKey: sections[currentIndex + 1].section_key, field: 'title' });
    } else {
      // At last section - create a new one
      handleInsert(currentKey);
    }
  }, [sections, setActiveField, handleInsert]);

  // Navigate from body to current section's title
  const handleNavigateToTitle = useCallback((currentKey: string) => {
    setActiveField({ sectionKey: currentKey, field: 'title' });
  }, [setActiveField]);

  // Navigate from title to current section's body
  const handleNavigateToBody = useCallback((currentKey: string) => {
    setActiveField({ sectionKey: currentKey, field: 'body' });
  }, [setActiveField]);

  // Navigate from title to previous section's body
  const handleNavigatePrevFromTitle = useCallback((currentKey: string) => {
    const currentIndex = sections.findIndex(s => s.section_key === currentKey);
    if (currentIndex > 0) {
      setActiveField({ sectionKey: sections[currentIndex - 1].section_key, field: 'body' });
      // Signal to position cursor at bottom of previous section's body
      window.dispatchEvent(new CustomEvent('section-cursor-position', {
        detail: { x: 0, fromBottom: false }
      }));
    }
  }, [sections, setActiveField]);

  return {
    handleNavigateNextFromBody,
    handleShiftReturnFromBody,
    handleNavigateToTitle,
    handleNavigateToBody,
    handleNavigatePrevFromTitle,
    handleInsert,
  };
}
